import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from '@chakra-ui/react';
import React, { useCallback, useRef, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';

export const ConfirmDeleteModal = ({
  isOpen,
  propertyName,
  onClose,
  onDelete,
}: {
  isOpen: boolean;
  propertyName?: string;
  onClose: () => void;
  onDelete: () => void;
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      size="lg"
      returnFocusOnClose={false}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm Delete
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you would like to delete {propertyName}?
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={onDelete} ml={3}>
              Confirm Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

type SetConfirmDeleteFn = (name: string, callback: () => void) => void;
const ConfirmDeleteContext = React.createContext<{
  showConfirmDeleteModal: SetConfirmDeleteFn;
}>({ showConfirmDeleteModal: () => {} });

type SetConfirmDeletePayload = { name: string; callback: () => void };

export const ConfirmDeleteModalProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [
    confirmDeleteFn,
    setConfirmDeleteFn,
  ] = useState<SetConfirmDeletePayload | null>(null);

  const showConfirmDeleteModal = useCallback(
    (name: string, callback: () => void) => {
      setConfirmDeleteFn({ name, callback });
    },
    [],
  );

  return (
    <ConfirmDeleteContext.Provider value={{ showConfirmDeleteModal }}>
      <ConfirmDeleteModal
        isOpen={Boolean(confirmDeleteFn)}
        propertyName={confirmDeleteFn?.name}
        onClose={() => setConfirmDeleteFn(null)}
        onDelete={() => {
          confirmDeleteFn?.callback();
          setConfirmDeleteFn(null);
        }}
      />
      {children}
    </ConfirmDeleteContext.Provider>
  );
};

export const useConfirmDeleteContext = () => {
  const context = React.useContext(ConfirmDeleteContext);

  finleyAssert(
    context,
    'ConfirmDeleteContext must be used within a ConfirmDeleteContext',
  );

  return context;
};
